import {
    Component,
    ChangeDetectionStrategy,
    OnInit,
    ViewChild,
    ChangeDetectorRef,
    OnDestroy
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { FormHelperService, ValidatorPipe, isEditOrRelistPage, isEditPage, isRelistPage } from '@classifieds-sell/shared';
import { MotorsDraftFacade } from '@classifieds-sell/state';
import { Subject } from 'rxjs';
import { MotorsSectionBase } from '../motors-section-base';
import { OptionItem, SellingTypeEnum } from '@classifieds-sell/datalayer';
import { ActivatedRoute } from '@angular/router';

@Component({
    selector: 'motors-ending-datetime-section',
    templateUrl: './motors-ending-datetime-section.component.html',
    styleUrls: ['./motors-listing-details.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class MotorsEndingDateTimeSectionComponent
    extends MotorsSectionBase
    implements OnInit, OnDestroy {
    private _destroyed$: Subject<void> = new Subject<void>();
    minDate = new Date();
    isEditPage = false;
    isRelistPage = false;
    inputDisabled = false;
    hasBeenExtended = false;

    @ViewChild('motorsEndingDatetimeSectionForm')
    motorsEndingDatetimeSectionForm: NgForm;

    constructor(
        public draftFacade: MotorsDraftFacade,
        private _changeDetectorRef: ChangeDetectorRef,
        private _formHelperService: FormHelperService,
        private _validatorPipe: ValidatorPipe,
        private route: ActivatedRoute
    ) {
        super(draftFacade);
    }

    ngOnDestroy(): void {
        super.onDestroy();
        this._destroyed$.next();
    }

    ngOnInit(): void {
        super.onInit(() => {
            this.isEditPage = isEditPage(this.route, false);
            this.isRelistPage = isRelistPage(this.route, false);
            this.hasBeenExtended = this.metadata.hasBeenExtended;
            if(this.isRelistPage && this.draftData.listingDetails.endDate === this.originalDraftData.listingDetails.endDate){
                this.draftData.listingDetails.endDate = this.maxDate();
            }
            this.setInputDisabled();
            this._changeDetectorRef.markForCheck();
        });
    }

    setInputDisabled(){
        this.inputDisabled = this.isEditPage === true
            && (this.draftData.listingDetails.sellingType === SellingTypeEnum.auction
            && this.metadata.bidCount > 0
            || this.hasBeenExtended);
    }

    isValid(): boolean {
        this._formHelperService.validate(this.motorsEndingDatetimeSectionForm);
        return this.formValidationWhenDisabled(this.motorsEndingDatetimeSectionForm);
    }

    protected formValidationWhenDisabled(ngForm: NgForm): boolean {
        // When there is a bid, this becomes greyed out and read-only then all inputs are disabled => form should be valid
        const isFormDisabled = ngForm.form.disabled;
        return (((isEditOrRelistPage(this.route) && isFormDisabled)
            || ngForm.form.valid));
    }

    dirty(): boolean {
        return this.motorsEndingDatetimeSectionForm.form.dirty;
    }

    auctionTimeChange() {
        this.motorsEndingDatetimeSectionForm?.form?.controls?.auctionTime?.updateValueAndValidity();
        this.motorsEndingDatetimeSectionForm?.form?.controls?.auctionTime?.markAsUntouched();
        this.publish();
    }

    updateEndDate(newDate: Date): void {
        this.draftData.listingDetails.endDate = newDate;
        this.publish();
    }

    maxDate() {
        const maxDate = new Date();

        if (this.isEditPage && this.metadata.listingStartDate) {
            const startDate = new Date(this.metadata.listingStartDate);
            maxDate.setHours(startDate.getHours(), startDate.getMinutes(), 0, 0);
        }

        maxDate.setDate(maxDate.getDate() + this.metadata.maxDurationDays);
        return maxDate;
    }

    minDateAndTime() {
        const minDateAndTime = new Date();
        minDateAndTime.setMinutes(this.minDate.getMinutes() + this.metadata.minDurationMinutes);
        return minDateAndTime;
    }

    auctionTimeValidation() {
        return this._validatorPipe.transform({
            name: 'End time',
            validations: {
                required: { value: true },
                customValidation: {
                    errorMessage: `End time has to be more than ${(this.metadata.minDurationMinutes / 60)} hours and less than ${this.metadata.maxDurationDays} days into the future`,
                    enable: true,
                    validate: (auctionTime: number) => {
                        const endDate = this.draftData.listingDetails.endDate;
                        // if the date value is not entered, validation will not fire
                        // auctionTime null means user has not selected a time
                        if (!endDate || auctionTime === null) {
                            return true;
                        }

                        const hour = Math.trunc(auctionTime);
                        const minute = auctionTime - hour;
                        const date = new Date(endDate);
                        date.setHours(hour, minute * 60, 0, 0);

                        const max = this.maxDate();
                        return date > this.minDateAndTime() && date <= max;
                    }
                }
            }
        });
    }
}